import { z } from "zod";

// https://clerk.com/docs/errors/frontend-api
// https://clerk.com/docs/errors/backend-api
export const ClerkErrorCodes = {
  INVITATION_ALREADY_EXISTS: "invitation_already_exists",
  ALREADY_A_MEMBER_IN_ORGANIZATION: "already_a_member_in_organization",
  ORGANIZATION_MEMBERSHIP_QUOTA_EXCEEDED:
    "organization_membership_quota_exceeded",
} as const;

export type ClerkInviteResult =
  | { success: true }
  | { success: false; email: string; error: string };

export type ClerkError = {
  status: number;
  clerkTraceId: string;
  clerkError: boolean;
  errors: {
    code: string;
    message: string;
    longMessage: string;
    meta: object;
  }[];
};

export function isClerkError(error: unknown): error is ClerkError {
  return (
    typeof error === "object" &&
    error !== null &&
    "clerkError" in error &&
    error["clerkError"] === true
  );
}

export const roleSchema = z.enum(["superAdmin", "admin", "dealer", "user"]);
export type Role = z.infer<typeof roleSchema>;

export const regionSchema = z.enum(["AU", "US"]);
export type Region = z.infer<typeof regionSchema>;
export const isAustraliaRegion = (region: Region) =>
  region === regionSchema.enum.AU;
export const isUSRegion = (region: Region) => region === regionSchema.enum.US;

const ORGANIZATION_ROLE_VALUES = {
  FARMER: "org:farmer",
  DEALER: "org:dealer",
} as const;
export const organizationRoleSchema = z.enum(ORGANIZATION_ROLE_VALUES);
export type OrganizationRole = z.infer<typeof organizationRoleSchema>;

export const userSchema = z.object({
  id: z.string(),
  email: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  role: roleSchema,
  isAdmin: z.boolean(),
  isDealer: z.boolean(),
  hasProfile: z.boolean(),
  region: regionSchema,
  organizations: z.array(z.object({ id: z.string(), name: z.string() })),
  lastSignInAt: z.number().nullish(),
  imageUrl: z.string(),
});
export type User = z.infer<typeof userSchema>;

// `role` is deliberately a plain string, not organizationRoleSchema: this
// data comes straight from Clerk's org membership/invitation APIs, which can
// return Clerk's own built-in roles (e.g. "org:admin", auto-assigned to
// whoever creates an org) outside our custom farmer/dealer role set.
// Narrowing this would reject legitimate Clerk data.
export const organizationMemberSchema = z.object({
  userId: z.string(),
  email: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  role: z.string(),
});
export type OrganizationMember = z.infer<typeof organizationMemberSchema>;

export const invitationSchema = z.object({
  email: z.string(),
  role: z.string(),
  invitationId: z.string(),
  createdAt: z.number(),
});
export type Invitation = z.infer<typeof invitationSchema>;

export type MemberInvitedEventData = {
  email: string;
  role: OrganizationRole;
};

export type MemberInvitationRevokedEventData = {
  email: string;
  role: OrganizationRole;
};

export type MemberAddedEventData = {
  email: string;
  role: OrganizationRole;
  userId: string;
};

export type MemberRemovedEventData = {
  email: string;
  role: OrganizationRole;
  userId: string;
};
